import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { SOCKET_EVENTS } from '@projectflow/types';
import type {
  SocketEventName,
  TaskEventPayload,
  TaskStatusChangedPayload,
  ProjectTasksChangedPayload,
  ListChangedPayload,
  PresencePayload,
} from '@projectflow/types';

// The API's `connect_error` message for a refused token (ProjectGateway).
const UNAUTHORIZED = 'unauthorized';

export interface SocketAuth {
  getToken: () => string | null;
  // Resolves true once a fresh token is in place.
  refresh: () => Promise<boolean>;
}

let socket: Socket | null = null;
let auth: SocketAuth | null = null;
// The server forgets a socket's rooms when it disconnects, so every room
// this tab is in is joined again on each (re)connection.
const joinedProjects = new Set<string>();
const reconnectHandlers = new Set<() => void>();

// What pages on this tab watch without viewing a board. Each registration is
// kept apart so one page stopping doesn't stop another's; the server is told
// the union (projects, lists) or the latest (organization).
const projectWatches = new Map<number, string[]>();
const listWatches = new Map<number, string[]>();
const organizationWatches = new Map<number, string | null>();
let nextWatchId = 0;

const unionOf = (watches: Map<number, string[]>) => [...new Set([...watches.values()].flat())];
const latestOf = (watches: Map<number, string | null>) => [...watches.values()].at(-1) ?? null;

function sendProjectWatches(s: Socket) {
  s.emit(SOCKET_EVENTS.WATCH_PROJECTS, { projectIds: unionOf(projectWatches) });
}
function sendListWatches(s: Socket) {
  s.emit(SOCKET_EVENTS.WATCH_LISTS, { listIds: unionOf(listWatches) });
}
function sendOrganizationWatch(s: Socket) {
  s.emit(SOCKET_EVENTS.WATCH_ORGANIZATION, { organizationId: latestOf(organizationWatches) });
}

function createSocket(): Socket {
  const s = io(import.meta.env['VITE_WS_URL'] ?? '/', {
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 10,
  });
  // Read on every (re)connection, so a reconnect sends the current token
  // rather than the one from sign-in, which expires after 15 minutes.
  s.auth = (cb) => cb({ token: auth?.getToken() ?? null });

  let connectedBefore = false;
  let refreshedSinceConnect = false;
  s.on('connect', () => {
    refreshedSinceConnect = false;
    for (const projectId of joinedProjects) s.emit(SOCKET_EVENTS.JOIN_PROJECT, { projectId });
    if (projectWatches.size > 0) sendProjectWatches(s);
    if (listWatches.size > 0) sendListWatches(s);
    if (organizationWatches.size > 0) sendOrganizationWatch(s);
    // Events sent while disconnected are lost; subscribers refetch instead.
    if (connectedBefore) reconnectHandlers.forEach((handler) => handler());
    connectedBefore = true;
  });
  // socket.io doesn't retry a connection the server refused, so refresh the
  // token and try again — once, so a token that's refused even when fresh
  // can't loop.
  s.on('connect_error', (err) => {
    if (err.message !== UNAUTHORIZED || refreshedSinceConnect || !auth) return;
    refreshedSinceConnect = true;
    void auth.refresh().then((ok) => {
      if (ok && !s.active) s.connect();
    });
  });
  s.on('disconnect', (reason) => {
    console.warn('Socket disconnected:', reason);
  });
  return s;
}

export function getSocket(): Socket {
  socket ??= createSocket();
  return socket;
}

// Safe to call on every sign-in and token refresh: an open or reconnecting
// socket is left alone and picks up the new token on its next connection.
export function connectSocket(socketAuth: SocketAuth): Socket {
  auth = socketAuth;
  const s = getSocket();
  if (!s.active) s.connect();
  return s;
}

export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
  auth = null;
  joinedProjects.clear();
  projectWatches.clear();
  listWatches.clear();
  organizationWatches.clear();
}

// Sent with API requests so the API leaves this tab out of the events its
// own changes cause: it has already updated optimistically.
export function getSocketId(): string | undefined {
  return socket?.connected ? socket.id : undefined;
}

// Called after the socket reconnects, when events may have been missed.
export function onReconnect(handler: () => void): () => void {
  reconnectHandlers.add(handler);
  return () => {
    reconnectHandlers.delete(handler);
  };
}

// ── Room management ────────────────────────────────────────────────────────
//
// Joins and leaves are only sent while connected; a join made before then
// is sent by the connect handler.

export function joinProjectRoom(projectId: string) {
  joinedProjects.add(projectId);
  const s = getSocket();
  if (s.connected) s.emit(SOCKET_EVENTS.JOIN_PROJECT, { projectId });
}

export function leaveProjectRoom(projectId: string) {
  joinedProjects.delete(projectId);
  const s = getSocket();
  if (s.connected) s.emit(SOCKET_EVENTS.LEAVE_PROJECT, { projectId });
}

export function sendHeartbeat(projectId: string) {
  const s = getSocket();
  if (s.connected) s.emit(SOCKET_EVENTS.PRESENCE_HEARTBEAT, { projectId });
}

// Each returns a function that stops this page's watch. Changes are only
// sent while connected; the connect handler sends the rest.

function register<T>(watches: Map<number, T>, value: T, send: (s: Socket) => void): () => void {
  const id = nextWatchId++;
  watches.set(id, value);
  const sendIfConnected = () => {
    if (socket?.connected) send(socket);
  };
  sendIfConnected();
  return () => {
    watches.delete(id);
    sendIfConnected();
  };
}

// Task events for these projects, without showing as viewing them.
export const watchProjects = (projectIds: string[]) =>
  register(projectWatches, projectIds, sendProjectWatches);

export const watchLists = (listIds: string[]) => register(listWatches, listIds, sendListWatches);

// The organization whose lists overview this tab shows.
export const watchOrganization = (organizationId: string | null) =>
  register(organizationWatches, organizationId, sendOrganizationWatch);

// ── Typed event listeners ──────────────────────────────────────────────────

function listen<T>(event: SocketEventName, handler: (payload: T) => void): () => void {
  const s = getSocket();
  s.on(event, handler);
  return () => {
    s.off(event, handler);
  };
}

export const onTaskCreated = (handler: (payload: TaskEventPayload) => void) =>
  listen(SOCKET_EVENTS.TASK_CREATED, handler);

export const onTaskUpdated = (handler: (payload: TaskEventPayload) => void) =>
  listen(SOCKET_EVENTS.TASK_UPDATED, handler);

export const onTaskDeleted = (handler: (payload: TaskEventPayload) => void) =>
  listen(SOCKET_EVENTS.TASK_DELETED, handler);

export const onTaskStatusChanged = (handler: (payload: TaskStatusChangedPayload) => void) =>
  listen(SOCKET_EVENTS.TASK_STATUS_CHANGED, handler);

export const onTasksChanged = (handler: (payload: ProjectTasksChangedPayload) => void) =>
  listen(SOCKET_EVENTS.TASKS_CHANGED, handler);

export const onListChanged = (handler: (payload: ListChangedPayload) => void) =>
  listen(SOCKET_EVENTS.LIST_CHANGED, handler);

export const onListsChanged = (handler: () => void) => listen(SOCKET_EVENTS.LISTS_CHANGED, handler);

export const onPresenceJoined = (handler: (payload: PresencePayload) => void) =>
  listen(SOCKET_EVENTS.PRESENCE_JOINED, handler);

export const onPresenceLeft = (handler: (payload: PresencePayload) => void) =>
  listen(SOCKET_EVENTS.PRESENCE_LEFT, handler);
